import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FETCH_FORMS_LIST } from 'actions/formActions';
import { 
    FaSearch, 
    FaTimes, 
    FaChargingStation, 
    FaCheckCircle, 
    FaClock, 
    FaFileAlt, 
    FaTrash,
    FaUserTie,
    FaUserCheck,
    FaShieldAlt,
    FaFileInvoice,
    FaClipboardList
} from 'react-icons/fa';
import { deleteStation } from 'api/formApi';
import { toast } from 'react-toastify';
import { cookie } from 'utils/cookies';
import Pagination from 'components/general/Pagination';

const ShimmerLoader = () => {
    return (
        <div className="glass-panel shadow-sm p-4" style={{ overflow: 'hidden', background: '#fff', borderRadius: '16px' }}>
            <style dangerouslySetInnerHTML={{__html: `
                @keyframes shimmer {
                    0% {
                        background-position: -468px 0;
                    }
                    100% {
                        background-position: 468px 0;
                    }
                }
                .shimmer-element {
                    animation: shimmer 1.5s linear infinite;
                    background: linear-gradient(to right, #f1f5f9 8%, #e2e8f0 18%, #f1f5f9 33%);
                    background-size: 800px 104px;
                    position: relative;
                }
            `}} />
            <div className="table-responsive">
                <table className="premium-table" style={{ pointerEvents: 'none', width: '100%' }}>
                    <thead>
                        <tr>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '120px', borderRadius: '4px' }}></div></th>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '150px', borderRadius: '4px' }}></div></th>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '100px', borderRadius: '4px' }}></div></th>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '80px', borderRadius: '4px' }}></div></th>
                            <th style={{ textAlign: 'center' }}><div className="shimmer-element m-auto" style={{ height: '16px', width: '80px', borderRadius: '4px' }}></div></th>
                        </tr>
                    </thead>
                    <tbody>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <tr key={i}>
                                <td className="py-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="shimmer-element" style={{ width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0 }}></div>
                                        <div className="shimmer-element" style={{ height: '16px', width: '120px', borderRadius: '4px' }}></div>
                                    </div>
                                </td>
                                <td className="py-3">
                                    <div className="shimmer-element" style={{ height: '14px', width: '180px', borderRadius: '4px' }}></div>
                                </td>
                                <td className="py-3">
                                    <div className="shimmer-element" style={{ height: '14px', width: '100px', borderRadius: '4px' }}></div>
                                </td>
                                <td className="py-3">
                                    <div className="shimmer-element" style={{ height: '24px', width: '90px', borderRadius: '20px' }}></div>
                                </td>
                                <td className="py-3" style={{ textAlign: 'center' }}>
                                    <div className="d-flex gap-2 justify-content-center">
                                        <div className="shimmer-element" style={{ width: '32px', height: '32px', borderRadius: '8px' }}></div>
                                        <div className="shimmer-element" style={{ width: '32px', height: '32px', borderRadius: '8px' }}></div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const isQualityPending = (f) => {
    const s = (f.status || '').toLowerCase();
    const s1 = (f.l1?.status || '').toLowerCase();
    const s2 = (f.l2?.status || '').toLowerCase();
    return s === 'quality' || s.includes('quality') || s1 === 'quality' || s1.includes('quality') || s2 === 'quality' || s2.includes('quality');
};

const isManagerPending = (f) => {
    const s = (f.status || '').toLowerCase();
    const s1 = (f.l1?.status || '').toLowerCase();
    const s2 = (f.l2?.status || '').toLowerCase();
    return s === 'manager' || s === 'pending_manager' || s === 'pending' || s === 'submitted' || s1.includes('manager') || s2.includes('manager');
};

const isContractorPending = (f) => {
    const s = (f.status || '').toLowerCase();
    const s1 = (f.l1?.status || '').toLowerCase();
    const s2 = (f.l2?.status || '').toLowerCase();
    return s === 'contractor' || s1 === 'contractor' || s2 === 'contractor';
};

const FormList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, allFormsList } = useSelector((state) => state.checklist);
    const [search, setSearch] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [pendingRoleFilter, setPendingRoleFilter] = useState(''); // '' | 'quality' | 'manager' | 'contractor'
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const limit = 10;
    const userRole = cookie.get('user-role');

    useEffect(() => {
        dispatch({ type: FETCH_FORMS_LIST });
    }, [dispatch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, userFilter, pendingRoleFilter]);

    const handleDeleteStation = async (stationId) => {
        if (window.confirm(`Are you sure you want to delete station "${stationId}" and all its associated forms? This action cannot be undone.`)) {
            try {
                const response = await deleteStation(stationId);
                if (response.status === 200) {
                    toast.success('Station and associated forms deleted successfully');
                    dispatch({ type: FETCH_FORMS_LIST }); // Refresh list
                } else {
                    toast.error(response.message || 'Failed to delete station');
                }
            } catch (error) {
                toast.error(error.message || 'An error occurred while deleting the station');
            }
        }
    };

    // Group forms by stationId
    const groupedByStation = useMemo(() => {
        return (allFormsList || []).filter(f => {
            if (userRole === 'quality') {
                return isQualityPending(f);
            }
            return true;
        }).reduce((acc, form) => {
            if (!form.stationId || typeof form.stationId !== 'string') return acc;
            if (!acc[form.stationId]) acc[form.stationId] = [];
            acc[form.stationId].push(form);
            return acc;
        }, {});
    }, [allFormsList, userRole]);

    const uniqueUsers = useMemo(() => {
        return Array.from(new Set((allFormsList || []).map(f => f.userName).filter(Boolean))).sort();
    }, [allFormsList]);

    // Build filtered and structured station data
    const filteredList = useMemo(() => {
        return Object.entries(groupedByStation)
            .filter(([, forms]) => forms.length > 0)
            .filter(([stationId, forms]) => {
                const stationMatch = stationId.toLowerCase().includes(search.toLowerCase());
                const userMatchInSearch = forms[0].userName?.toLowerCase().includes(search.toLowerCase());
                const projectMatchInSearch = forms[0].project_name?.toLowerCase().includes(search.toLowerCase());
                
                const matchesSearch = stationMatch || userMatchInSearch || projectMatchInSearch;
                const matchesUserFilter = !userFilter || forms[0].userName === userFilter;

                let matchesRoleFilter = true;
                if (pendingRoleFilter === 'approved') {
                    matchesRoleFilter = forms.some(f => (f.status || '').toLowerCase() === 'approved');
                } else if (pendingRoleFilter === 'pending') {
                    matchesRoleFilter = forms.some(f => {
                        const s = (f.status || '').toLowerCase();
                        return s !== 'approved' && s !== 'draft';
                    });
                } else if (pendingRoleFilter === 'draft') {
                    matchesRoleFilter = forms.some(f => (f.status || '').toLowerCase() === 'draft');
                } else if (pendingRoleFilter === 'quality') {
                    matchesRoleFilter = forms.some(isQualityPending);
                } else if (pendingRoleFilter === 'manager') {
                    matchesRoleFilter = forms.some(isManagerPending);
                } else if (pendingRoleFilter === 'contractor') {
                    matchesRoleFilter = forms.some(isContractorPending);
                }
                
                return matchesSearch && matchesUserFilter && matchesRoleFilter;
            })
            .map(([stationId, forms]) => {
                const totalForms = forms.length;
                const approvedForms = forms.filter((f) => (f.status || '').toLowerCase() === 'approved').length;
                const draftForms = forms.filter((f) => (f.status || '').toLowerCase() === 'draft').length;
                const pendingForms = totalForms - approvedForms - draftForms;

                const contractorPendingCount = forms.filter(isContractorPending).length;
                const managerPendingCount = forms.filter(isManagerPending).length;
                const qualityPendingCount = forms.filter(isQualityPending).length;

                const { project_name, userName } = forms[0];

                return {
                    stationId,
                    project_name: project_name || 'N/A',
                    userName: userName || 'N/A',
                    status: approvedForms === totalForms && totalForms > 0 ? 'completed' : 'in-progress',
                    formCount: totalForms,
                    approvedCount: approvedForms,
                    draftCount: draftForms,
                    pendingCount: pendingForms,
                    contractorPendingCount,
                    managerPendingCount,
                    qualityPendingCount,
                };
            })
            .sort((a, b) => a.stationId.localeCompare(b.stationId));
    }, [groupedByStation, search, userFilter, pendingRoleFilter]);

    const total = filteredList.length;
    const totalPages = Math.ceil(total / limit) || 1;

    const paginatedList = useMemo(() => {
        const start = (currentPage - 1) * limit;
        return filteredList.slice(start, start + limit);
    }, [filteredList, currentPage, limit]);

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected + 1);
    };

    // Filtered forms matching search & userFilter for top stats
    const allFilteredForms = useMemo(() => {
        return (allFormsList || []).filter(f => {
            if (userRole === 'quality') {
                return isQualityPending(f);
            }
            return true;
        }).filter(f => {
            const stationMatch = !search || 
                (f.stationId || '').toLowerCase().includes(search.toLowerCase()) || 
                (f.userName || '').toLowerCase().includes(search.toLowerCase()) || 
                (f.project_name || '').toLowerCase().includes(search.toLowerCase());
            const userMatch = !userFilter || f.userName === userFilter;
            return stationMatch && userMatch;
        });
    }, [allFormsList, userRole, search, userFilter]);

    // Summary stats
    const totalStations = Object.keys(groupedByStation).length;
    const totalApproved = allFilteredForms.filter(f => (f.status || '').toLowerCase() === 'approved').length;
    const totalDraft = allFilteredForms.filter(f => (f.status || '').toLowerCase() === 'draft').length;
    const totalPending = allFilteredForms.length - totalApproved - totalDraft;

    const totalQualityPending = allFilteredForms.filter(isQualityPending).length;
    const totalManagerPending = allFilteredForms.filter(isManagerPending).length;
    const totalContractorPending = allFilteredForms.filter(isContractorPending).length;

    const allStatCards = [
        { label: 'Total Stations', value: totalStations, icon: <FaChargingStation size={14} />, iconColor: '#475569', iconBg: '#f1f5f9', filterKey: 'all' },
        { label: 'Approved', value: totalApproved, icon: <FaCheckCircle size={14} />, iconColor: '#16a34a', iconBg: '#dcfce7', filterKey: 'approved' },
        { label: 'Total Pending', value: totalPending, icon: <FaClock size={14} />, iconColor: '#ea580c', iconBg: '#ffedd5', filterKey: 'pending' },
        { label: 'Draft', value: totalDraft, icon: <FaFileAlt size={14} />, iconColor: '#2563eb', iconBg: '#dbeafe', filterKey: 'draft' },
        { label: 'Pending Quality', value: totalQualityPending, icon: <FaShieldAlt size={14} />, iconColor: '#e11d48', iconBg: '#ffe4e6', filterKey: 'quality' },
        { label: 'Pending Manager', value: totalManagerPending, icon: <FaUserCheck size={14} />, iconColor: '#0284c7', iconBg: '#e0f2fe', filterKey: 'manager' },
        { label: 'Pending Contractor', value: totalContractorPending, icon: <FaUserTie size={14} />, iconColor: '#9333ea', iconBg: '#f3e8ff', filterKey: 'contractor' },
    ];

    return (
        <div className="page-body checklist-container">
            <div className="container-xl">


                {/* Compact Stat Cards Grid */}
                <div className="row g-2 mb-3">
                    {allStatCards.map((card) => {
                        const isSelected = card.filterKey === 'all' 
                            ? !pendingRoleFilter 
                            : pendingRoleFilter === card.filterKey;
                        return (
                            <div className="col-6 col-sm-4 col-md-3 col-xl" key={card.label}>
                                <div 
                                    onClick={() => {
                                        if (card.filterKey === 'all') {
                                            setPendingRoleFilter('');
                                        } else {
                                            setPendingRoleFilter(prev => prev === card.filterKey ? '' : card.filterKey);
                                        }
                                    }}
                                    style={{
                                        background: isSelected ? card.iconBg : '#ffffff',
                                        border: isSelected ? `2px solid ${card.iconColor}` : '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        padding: '10px 14px',
                                        boxShadow: isSelected 
                                            ? `0 2px 8px 0 ${card.iconColor}25` 
                                            : '0 1px 2px 0 rgba(0,0,0,0.04)',
                                        transition: 'all 0.15s ease',
                                        cursor: 'pointer',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}
                                    title={isSelected ? (card.filterKey === 'all' ? 'All stations currently shown' : 'Click to clear filter') : `Filter by ${card.label}`}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <span style={{ 
                                            color: isSelected ? card.iconColor : '#64748b', 
                                            fontSize: '11px', 
                                            fontWeight: 700, 
                                            textTransform: 'uppercase', 
                                            letterSpacing: '0.03em',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {card.label}
                                        </span>
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '26px',
                                            height: '26px',
                                            borderRadius: '8px',
                                            background: isSelected ? '#ffffff' : card.iconBg,
                                            color: card.iconColor,
                                            flexShrink: 0
                                        }}>{card.icon}</span>
                                    </div>
                                    <div style={{ fontSize: '20px', fontWeight: 800, lineHeight: 1.1, color: '#0f172a' }}>
                                        {card.value}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Search */}
                <div className="premium-filter-section mb-4">
                    <div className={`filter-group search-wrapper ${search || isSearchExpanded ? 'expanded' : ''}`}>
                        <label htmlFor="station-search">Search Stations</label>
                        <div className="position-relative">
                            <Form.Control
                                id="station-search"
                                type="text"
                                placeholder="Search by Station, Filer or Project..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onFocus={() => setIsSearchExpanded(true)}
                                onBlur={() => !search && setIsSearchExpanded(false)}
                            />
                            <FaSearch className="search-icon" />
                        </div>
                    </div>
                    <div className="filter-group select-wrapper">
                        <label htmlFor="user-filter">Filter by Filer</label>
                        <Form.Select
                            id="user-filter"
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                            style={{ borderRadius: '12px', border: '1px solid #e2e8f0', height: '42px', paddingLeft: '15px' }}
                        >
                            <option value="">All Filers</option>
                            {uniqueUsers.map(u => (
                                <option key={u} value={u}>{u}</option>
                            ))}
                        </Form.Select>
                    </div>
                    { (search || userFilter || pendingRoleFilter) && (
                        <div className="filter-group flex-grow-0">
                            <span className="filter-label-spacer" aria-hidden="true">&nbsp;</span>
                            <button id="clear-search-btn" className="btn-clear" onClick={() => { setSearch(''); setUserFilter(''); setPendingRoleFilter(''); }} title="Clear Filters">
                                <FaTimes /> Clear
                            </button>
                        </div>
                    )}
                </div>

                {/* Table */}
                {loading ? (
                    <ShimmerLoader />
                ) : (
                    <div className="table-responsive">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>Station</th>
                                    <th>Project</th>
                                    <th>Created By</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'center' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedList.map((form) => (
                                    <tr 
                                        key={form.stationId}
                                        onClick={() => navigate(`/formList/${form.stationId}`)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{
                                                    width: '36px', height: '36px', borderRadius: '10px',
                                                    background: '#f1f5f9',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    <FaChargingStation color="#475569" size={16} />
                                                </div>
                                                <div>
                                                    <span
                                                        className="fw-bold"
                                                        role="button"
                                                        tabIndex="0"
                                                        style={{ color: '#1e293b', fontSize: '14px' }}
                                                    >
                                                        {form.stationId}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="text-muted" style={{ fontSize: '13px' }}>{form.project_name}</span></td>
                                        <td><span className="text-muted" style={{ fontSize: '13px' }}>{form.userName}</span></td>
                                        <td>
                                            <span className={`status-pill status-${form.status}`} style={{ width: 'fit-content' }}>
                                                {form.status === 'completed' ? 'Completed' : 'In Progress'}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div className="action-btn-group" style={{ justifyContent: 'center', gap: '6px' }}>
                                                <button
                                                    className="btn-action-badge btn-sat"
                                                    title="Redirect to current SAT forms"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/formList/${form.stationId}`);
                                                    }}
                                                >
                                                    <FaFileAlt style={{ fontSize: '11px' }} /> SAT
                                                </button>
                                                <button
                                                    className="btn-action-badge btn-jms"
                                                    title="JMS Forms"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toast.info(`JMS forms for ${form.stationId}`);
                                                    }}
                                                >
                                                    <FaClipboardList style={{ fontSize: '11px' }} /> JMS
                                                </button>
                                                <button
                                                    className="btn-action-badge btn-invoice"
                                                    title="Invoice"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toast.info(`Invoice for ${form.stationId}`);
                                                    }}
                                                >
                                                    <FaFileInvoice style={{ fontSize: '11px' }} /> Invoice
                                                </button>
                                                {(userRole === 'admin' || userRole === 'manager') && (
                                                    <button
                                                        className="btn-premium btn-danger-soft"
                                                        title="Delete Station"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteStation(form.stationId);
                                                        }}
                                                        style={{ background: '#fee2e2', color: '#ef4444', height: '31px', width: '31px', borderRadius: '8px' }}
                                                    >
                                                        <FaTrash size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredList.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="text-center py-5">
                                            <div className="d-flex flex-column align-items-center">
                                                <FaChargingStation size={48} className="text-muted mb-3" style={{ opacity: 0.3 }} />
                                                <p className="text-muted fw-bold mb-1">No stations found</p>
                                                <p className="text-muted small">Try adjusting your search criteria</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && total > 0 && (
                    <div className="mt-4 d-flex justify-content-end">
                        <Pagination
                            total={total}
                            handlePageClick={handlePageClick}
                            currentPage={currentPage}
                            limit={limit}
                            totalPages={totalPages}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormList;